import React from 'react';
import { 
  TrendingUp, DollarSign, Calendar, FileText, Users, Building, 
  ArrowUpRight, ArrowDownRight, Activity 
} from 'lucide-react';
import './ResultsPanel.css';
import VisualizationComponent from './VisualizationComponent';

// Table and Chart components removed as they are not used
// Table functionality is now handled by DataTable component
// Chart functionality is now handled by VisualizationComponent

const Insights = ({ insights }) => {
  if (!insights || !insights.insights || insights.insights.length === 0) return null;
  return (
    <div className="insights-container">
      <h4>Insights</h4>
      <ul>
        {insights.insights.map((insight, idx) => <li key={idx}>{insight}</li>)}
      </ul>
    </div>
  );
};

const ResultsPanel = ({ result, qbConnected }) => {
  if (!result) {
    return (
      <div className="results-panel-content">
        <div className="results-empty">
          <FileText size={48} className="empty-icon" />
          <h3>Results will appear here</h3>
          {qbConnected ? (
            <p>Send a message to see financial data, charts, and insights.</p>
          ) : (
            <p>Connect QuickBooks to analyze your financial data, or view your conversation history.</p>
          )}
          <div className="example-queries">
            <p>Try asking:</p>
            <ul>
              <li>"Show me my profit and loss for this quarter"</li>
              <li>"What are my recent transactions?"</li>
              <li>"Display my balance sheet"</li>
              <li>"Show me my top customers"</li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  if (result.error) {
    return (
      <div className="results-panel-content">
        <div className="error-message">{result.error}</div>
      </div>
    );
  }

  // Add user-friendly message for missing/empty viz or data
  const hasViz = !!result.visualization && result.visualization.data && result.visualization.data.length > 0;
  const hasData = !!result.data && Object.keys(result.data).length > 0;
  


  return (
    <div className="results-panel-content">
      <div className="results-header">
        <h3>Analysis Results</h3>
        <div className="results-sources">
          {result.sources && Array.isArray(result.sources) && result.sources.map((source, index) => (
            <span key={index} className="source-tag">
              {source === 'quickbooks' ? 'QuickBooks' : source}
            </span>
          ))}
        </div>
      </div>

      <div className="results-body">
        {/* Summary Cards */}
        {result.data && <SummaryCards data={result.data} />}
        {/* Visualization */}
        {hasViz ? (
          <VisualizationComponent viz={result.visualization} />
        ) : (
          <div className="viz-empty">No visualization available for this result.</div>
        )}
        {/* Raw Data Tables */}
        {hasData ? <DataTable data={result.data} /> : <div className="data-empty">No data available for this result.</div>}
        {result.table && <DataTable data={result.table} />}
        {result.insights && <Insights insights={result.insights} />}
        

      </div>
    </div>
  );
};

const SummaryCards = ({ data }) => {
  const metrics = extractMetrics(data);
  
  if (metrics.length === 0) return null;
  
  return (
    <div className="summary-cards">
      {metrics.map((metric, index) => (
        <div key={index} className="summary-card">
          <div className="metric-icon">
            {metric.type === 'revenue' && <DollarSign size={20} />}
            {metric.type === 'trend' && <TrendingUp size={20} />}
            {metric.type === 'period' && <Calendar size={20} />}
            {metric.type === 'customers' && <Users size={20} />}
            {metric.type === 'vendors' && <Building size={20} />}
            {metric.type === 'transactions' && <Activity size={20} />}
          </div>
          <div className="metric-content">
            <div className="metric-value">{metric.value}</div>
            <div className="metric-label">{metric.label}</div>
            {metric.change && (
              <div className={`metric-change ${metric.change > 0 ? 'positive' : 'negative'}`}>
                {metric.change > 0 ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
                {Math.abs(metric.change)}%
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

const DataTable = ({ data }) => {
  if (!data) return null;
  
  // Handle new structured table data (array of objects)
  if (Array.isArray(data) && data.length > 0) {
    const columns = Object.keys(data[0]);
    
    // Helper function to format cell values
    const formatCellValue = (value, columnName) => {
      if (value === null || value === undefined) return '';
      
      // If it's already formatted as currency, return as is
      if (typeof value === 'string' && value.startsWith('$')) {
        return value;
      }
      
      // If it's a number and looks like currency, format it
      if (typeof value === 'number' || (typeof value === 'string' && !isNaN(parseFloat(value)))) {
        const numValue = parseFloat(value);
        if (columnName.toLowerCase().includes('revenue') || 
            columnName.toLowerCase().includes('amount') || 
            columnName.toLowerCase().includes('total') ||
            columnName.toLowerCase().includes('balance')) {
          return `$${numValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        }
      }
      
      return value;
    };
    
    return (
      <div className="data-table-container">
        <h4>Data Table</h4>
        <div className="data-table">
          <table>
            <thead>
              <tr>
                {columns.map((col, idx) => (
                  <th key={idx}>{col.charAt(0).toUpperCase() + col.slice(1).replace(/_/g, ' ')}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.slice(0, 20).map((row, index) => (
                <tr key={index}>
                  {columns.map((col, idx) => (
                    <td key={idx}>
                      {formatCellValue(row[col], col)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }
  
  // Handle different data structures
  if (data.transactions && Array.isArray(data.transactions)) {
    return (
      <div className="data-table-container">
        <h4>Recent Transactions</h4>
        <div className="data-table">
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Description</th>
                <th>Amount</th>
                <th>Type</th>
                <th>Account</th>
              </tr>
            </thead>
            <tbody>
              {data.transactions.slice(0, 20).map((transaction, index) => (
                <tr key={index}>
                  <td>{formatDate(transaction.TxnDate || transaction.date)}</td>
                  <td>{transaction.Description || transaction.description}</td>
                  <td className={transaction.Amount < 0 ? 'negative' : 'positive'}>
                    ${Math.abs(transaction.Amount || transaction.amount).toLocaleString()}
                  </td>
                  <td>{transaction.TxnType || transaction.type}</td>
                  <td>{transaction.Account || transaction.account}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  if (data.profit_loss && data.profit_loss.Rows && Array.isArray(data.profit_loss.Rows)) {
    return (
      <div className="data-table-container">
        <h4>Profit & Loss Statement</h4>
        <div className="data-table">
          <table>
            <thead>
              <tr>
                <th>Account</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              {data.profit_loss.Rows.map((row, index) => {
                if (row.ColData && row.ColData.length >= 2) {
                  const label = row.ColData[0]?.value;
                  const value = parseFloat(row.ColData[1]?.value || 0);
                  return (
                    <tr key={index} className={row.type === 'Section' ? 'section-header' : ''}>
                      <td>{label}</td>
                      <td className={value < 0 ? 'negative' : 'positive'}>
                        ${Math.abs(value).toLocaleString()}
                      </td>
                    </tr>
                  );
                }
                return null;
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  if (data.balance_sheet && data.balance_sheet.Rows && Array.isArray(data.balance_sheet.Rows)) {
    return (
      <div className="data-table-container">
        <h4>Balance Sheet</h4>
        <div className="data-table">
          <table>
            <thead>
              <tr>
                <th>Account</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              {data.balance_sheet.Rows.map((row, index) => {
                if (row.ColData && row.ColData.length >= 2) {
                  const label = row.ColData[0]?.value;
                  const value = parseFloat(row.ColData[1]?.value || 0);
                  return (
                    <tr key={index} className={row.type === 'Section' ? 'section-header' : ''}>
                      <td>{label}</td>
                      <td className={value < 0 ? 'negative' : 'positive'}>
                        ${Math.abs(value).toLocaleString()}
                      </td>
                    </tr>
                  );
                }
                return null;
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  if (data.customers) {
    return (
      <div className="data-table-container">
        <h4>Customers</h4>
        <div className="data-table">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Balance</th>
              </tr>
            </thead>
            <tbody>
              {data.customers.slice(0, 20).map((customer, index) => (
                <tr key={index}>
                  <td>{customer.DisplayName || customer.name}</td>
                  <td>{customer.PrimaryEmailAddr?.Address || customer.email}</td>
                  <td>{customer.PrimaryPhone?.FreeFormNumber || customer.phone}</td>
                  <td>${(customer.Balance || customer.balance || 0).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // Handle new structured table data from backend
  if (Array.isArray(data) && data.length > 0 && typeof data[0] === 'object') {
    const columns = Object.keys(data[0]);
    return (
      <div className="data-table-container">
        <h4>Data Table</h4>
        <div className="data-table">
          <table>
            <thead>
              <tr>
                {columns.map((col, idx) => (
                  <th key={idx}>{col.charAt(0).toUpperCase() + col.slice(1)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.slice(0, 20).map((row, index) => (
                <tr key={index}>
                  {columns.map((col, idx) => {
                    const value = row[col];
                    if (typeof value === 'number') {
                      return (
                        <td key={idx} className={value < 0 ? 'negative' : 'positive'}>
                          ${Math.abs(value).toLocaleString()}
                        </td>
                      );
                    }
                    return <td key={idx}>{value}</td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  if (data.vendors) {
    return (
      <div className="data-table-container">
        <h4>Vendors</h4>
        <div className="data-table">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Balance</th>
              </tr>
            </thead>
            <tbody>
              {data.vendors.slice(0, 20).map((vendor, index) => (
                <tr key={index}>
                  <td>{vendor.DisplayName || vendor.name}</td>
                  <td>{vendor.PrimaryEmailAddr?.Address || vendor.email}</td>
                  <td>{vendor.PrimaryPhone?.FreeFormNumber || vendor.phone}</td>
                  <td>${(vendor.Balance || vendor.balance || 0).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  if (data.invoices) {
    return (
      <div className="data-table-container">
        <h4>Invoices</h4>
        <div className="data-table">
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Customer</th>
                <th>Invoice #</th>
                <th>Amount</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {data.invoices.slice(0, 20).map((invoice, index) => (
                <tr key={index}>
                  <td>{formatDate(invoice.TxnDate || invoice.date)}</td>
                  <td>{invoice.CustomerRef?.name || invoice.customer}</td>
                  <td>{invoice.DocNumber || invoice.number}</td>
                  <td>${(invoice.TotalAmt || invoice.amount || 0).toLocaleString()}</td>
                  <td>
                    <span className={`status ${invoice.Balance === 0 ? 'paid' : 'pending'}`}>
                      {invoice.Balance === 0 ? 'Paid' : 'Pending'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  if (data.expenses && Array.isArray(data.expenses)) {
    return (
      <div className="data-table-container">
        <h4>Expenses</h4>
        <div className="data-table">
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Vendor</th>
                <th>Description</th>
                <th>Amount</th>
                <th>Account</th>
              </tr>
            </thead>
            <tbody>
              {data.expenses.slice(0, 20).map((expense, index) => (
                <tr key={index}>
                  <td>{formatDate(expense.TxnDate || expense.date)}</td>
                  <td>{expense.VendorRef?.name || expense.vendor}</td>
                  <td>{expense.Description || expense.description}</td>
                  <td className="negative">${(expense.TotalAmt || expense.amount || 0).toLocaleString()}</td>
                  <td>{expense.AccountRef?.name || expense.account}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  if (data.revenue_by_customer) {
    return (
      <div className="data-table-container">
        <h4>Revenue by Customer</h4>
        <div className="data-table">
          <table>
            <thead>
              <tr>
                <th>Customer</th>
                <th>Total Revenue</th>
                <th>Invoices</th>
                <th>Sales Receipts</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(data.revenue_by_customer)
                .sort(([,a], [,b]) => b.total_revenue - a.total_revenue)
                .slice(0, 20)
                .map(([customer, data], index) => (
                <tr key={index}>
                  <td>{customer}</td>
                  <td className="positive">${data.total_revenue.toLocaleString()}</td>
                  <td>{data.invoices}</td>
                  <td>{data.sales_receipts}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }
  
  return null;
};

// Helper function to extract key metrics from data
const extractMetrics = (data) => {
  const metrics = [];
  
  if (data.profit_loss && data.profit_loss.Rows && Array.isArray(data.profit_loss.Rows)) {
    const rows = data.profit_loss.Rows;
    let totalRevenue = 0;
    let totalExpenses = 0;
    
    rows.forEach(row => {
      if (row.ColData && row.ColData.length >= 2) {
        const label = row.ColData[0]?.value;
        const value = parseFloat(row.ColData[1]?.value || 0);
        
        if (label && label.toLowerCase().includes('revenue')) {
          totalRevenue += value;
        } else if (label && label.toLowerCase().includes('expense')) {
          totalExpenses += value;
        }
      }
    });
    
    if (totalRevenue > 0) {
      metrics.push({
        type: 'revenue',
        label: 'Total Revenue',
        value: `$${totalRevenue.toLocaleString()}`
      });
    }
    
    if (totalExpenses > 0) {
      metrics.push({
        type: 'revenue',
        label: 'Total Expenses',
        value: `$${totalExpenses.toLocaleString()}`
      });
    }
    
    const netIncome = totalRevenue - totalExpenses;
    if (netIncome !== 0) {
      metrics.push({
        type: 'trend',
        label: 'Net Income',
        value: `$${netIncome.toLocaleString()}`,
        change: netIncome > 0 ? 12 : -8
      });
    }
  }
  
  if (data.transactions && Array.isArray(data.transactions)) {
    metrics.push({
      type: 'transactions',
      label: 'Recent Transactions',
      value: data.transactions.length.toString()
    });
  }
  
  if (data.customers && Array.isArray(data.customers)) {
    metrics.push({
      type: 'customers',
      label: 'Total Customers',
      value: data.customers.length.toString()
    });
  }
  
  if (data.vendors && Array.isArray(data.vendors)) {
    metrics.push({
      type: 'vendors',
      label: 'Total Vendors',
      value: data.vendors.length.toString()
    });
  }

  if (data.expenses && Array.isArray(data.expenses)) {
    const totalExpenses = data.expenses.reduce((sum, expense) => {
      return sum + (expense.TotalAmt || expense.amount || 0);
    }, 0);
    
    metrics.push({
      type: 'revenue',
      label: 'Total Expenses',
      value: `$${totalExpenses.toLocaleString()}`
    });
    
    metrics.push({
      type: 'transactions',
      label: 'Expense Transactions',
      value: data.expenses.length.toString()
    });
  }
  
  if (data.company_info) {
    const companyInfo = data.company_info.QueryResponse?.CompanyInfo?.[0];
    if (companyInfo) {
      metrics.push({
        type: 'period',
        label: 'Company',
        value: companyInfo.CompanyName
      });
    }
  }
  
  if (data.revenue_by_customer) {
    const totalRevenue = data.total_revenue || 0;
    const totalCustomers = data.total_customers || Object.keys(data.revenue_by_customer).length;
    
    metrics.push({
      type: 'revenue',
      label: 'Total Revenue',
      value: `$${totalRevenue.toLocaleString()}`
    });
    
    metrics.push({
      type: 'customers',
      label: 'Total Customers',
      value: totalCustomers.toString()
    });
    
    if (totalCustomers > 0) {
      const avgRevenue = totalRevenue / totalCustomers;
      metrics.push({
        type: 'revenue',
        label: 'Avg Revenue/Customer',
        value: `$${avgRevenue.toLocaleString()}`
      });
    }
  }
  
  return metrics.slice(0, 4); // Limit to 4 metrics
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString();
};

export default ResultsPanel;